import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  Zap, AlertTriangle, Play, History, Settings as SettingsIcon, MessageCircle, Mail, Bell, Loader2, CheckCircle2, Send, ListFilter, Database,
} from "lucide-react";
import { toast } from "sonner";
import ConditionsBuilder from "@/components/ConditionsBuilder";
import RecipientsPreview from "@/components/RecipientsPreview";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: "#16a34a" },
  email: { label: "E-mail", icon: Mail, color: "#0891b2" },
  app_notification: { label: "Notificação no App", icon: Bell, color: "#7c3aed" },
};

const TRIGGER_HUMAN = (a) => {
  const t = a?.trigger || {};
  if (t.type === "manual") return "Lista dinâmica · disparo manual";
  if (t.type === "event") return `Quando ocorre: ${(t.params || {}).event || "?"}`;
  if (t.type === "cron") {
    const p = t.params || {};
    if (p.minute && String(p.minute).startsWith("*/")) return `A cada ${String(p.minute).split("/")[1]} min`;
    if (p.hour != null && p.minute != null) {
      const dow = p.day_of_week || "diariamente";
      return `${dow} às ${String(p.hour).padStart(2, "0")}:${String(p.minute).padStart(2, "0")}`;
    }
    return "Periódico";
  }
  return "—";
};

const FUNNEL_LABELS = {
  cinza: "⚪ Sem contato",
  amarelo: "🟡 Educativa",
  laranja: "🟠 Convite agenda",
  verde: "🟢 Agendou",
  azul: "🔵 Atendida",
  vermelho: "🔴 Sem resposta",
};

export default function Automations() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [showRunsFor, setShowRunsFor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);  // {automation, matched, first_sample, rendered_preview, ...}
  const tab = searchParams.get("tab") || "ativas";
  const setTab = (t) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", t);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall("get", "/automations");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar automações");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { load(); }, [load]);

  const pendingReview = useMemo(
    () => items.filter((a) => !a.enabled && a.system_key && (a.stats?.total_sent || 0) === 0),
    [items]
  );

  const toggleEnabled = async (a) => {
    const newVal = !a.enabled;
    try {
      await apiCall("patch", `/automations/${a.automation_id}`, { enabled: newVal });
      setItems((prev) => prev.map((x) => x.automation_id === a.automation_id ? { ...x, enabled: newVal } : x));
      toast.success(newVal ? `"${a.name}" ATIVADA` : `"${a.name}" desativada`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao alterar");
    }
  };

  // Tenta mapear as condições da automação pros params de filtro de /clientes.
  // Se conseguir mapear, redireciona pro /clientes com o filtro aplicado +
  // diálogo de disparo em massa pré-carregado. Senão, retorna null pra cair
  // no fallback de /run-now (envio direto pelo backend).
  const tryMapToClientsFilter = (a) => {
    const conds = (a.conditions || []);
    let days = null;
    let kind = null; // "haircut" | "highlights"
    const visit = (list) => {
      for (const c of list) {
        if (c?.logic && Array.isArray(c.conditions)) {
          visit(c.conditions);
          continue;
        }
        const f = c?.field;
        const op = c?.op;
        const v = c?.value;
        if (f === "client.days_since_last_haircut" && (op === ">=" || op === ">" || op === "==")) {
          days = Number(v) || days;
          kind = "haircut";
        }
      }
    };
    visit(conds);
    // Heurística: se nome da automação menciona "luz/mecha/balayage/loira", mapeia pra no_highlights
    const nm = (a.name || "").toLowerCase();
    if (!kind && (nm.includes("luz") || nm.includes("mecha") || nm.includes("balayage") || nm.includes("loira") || nm.includes("highlight"))) {
      kind = "highlights";
      days = days || 180;
    }
    if (!kind || !days) return null;
    const params = new URLSearchParams();
    if (kind === "haircut") {
      params.set("filter", "no_haircut");
      params.set("days", String(days));
    } else if (kind === "highlights") {
      params.set("filter", "no_highlights");
      params.set("days", String(days));
    }
    params.set("bulk", "1");
    return params;
  };

  const testNow = async (a) => {
    // 🔒 PROTEÇÃO ANTI-ENVIO ACIDENTAL (jun/2026): se as condições casam com
    // filtros de /clientes, navegar pra lá com a lista ABERTA pra revisar antes
    // de enviar. Senão, chamar /test (que agora é DRY-RUN) e exibir o preview.
    const mapped = tryMapToClientsFilter(a);
    if (mapped) {
      const msg = a.action?.default_message || a.action?.custom_message || "";
      navigate(`/clientes?${mapped.toString()}`, {
        state: { bulkInitialMessage: msg, from: `automation:${a.automation_id}` },
      });
      return;
    }
    // Fallback: /test é DRY-RUN — NÃO envia. Mostra preview num modal.
    setTestingId(a.automation_id);
    try {
      const r = await apiCall("post", `/automations/${a.automation_id}/test`);
      setPreviewData({ automation: a, ...r.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao gerar pré-visualização");
    } finally {
      setTestingId(null);
    }
  };

  const runNow = async (a) => {
    // Caminho preferido: redirecionar pro /clientes com filtro + diálogo aberto
    const mapped = tryMapToClientsFilter(a);
    if (mapped) {
      const msg = a.action?.default_message || a.action?.custom_message || "";
      navigate(`/clientes?${mapped.toString()}`, {
        state: { bulkInitialMessage: msg, from: `automation:${a.automation_id}` },
      });
      return;
    }
    // Fallback legado: dispara direto pelo backend (sem filtro mapeável)
    if (!window.confirm(`Disparar AGORA "${a.name}"? Vai enviar até ${a?.limits?.max_per_run || 200} mensagens. Quer mesmo?`)) return;
    setRunningId(a.automation_id);
    try {
      const r = await apiCall("post", `/automations/${a.automation_id}/run-now`);
      const { matched = 0, sent = 0, errors = 0 } = r.data || {};
      if (sent > 0) toast.success(`${sent} mensagem(ns) enviada(s) · ${errors} erro(s)`);
      else if (matched > 0) toast.warning(`${matched} casaram mas 0 foram enviadas (provavelmente dedupe). Veja Histórico.`);
      else toast.warning("Nenhum cliente casou com as condições.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao disparar");
    } finally {
      setRunningId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="automations-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Motor de Regras · No-code</div>
          <h1 className="text-2xl font-light tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
            Automações
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Crie regras visuais SE/ENTÃO que rodam sozinhas: confirmações, retornos, cobranças, boas-vindas, listas dinâmicas e mais. Sem JSON, sem código.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          style={{ background: "#d97706" }}
          className="text-white hover:opacity-90"
          data-testid="new-automation-btn"
        >
          <Zap className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Nova automação
        </Button>
      </div>

      {/* Tabs: Ativas | Histórico | Conferência da importação */}
      <div className="flex items-center gap-1 border-b border-border" data-testid="automations-tabs">
        {[
          { k: "ativas", label: "📋 Ativas", icon: Zap },
          { k: "historico", label: "📊 Histórico", icon: History },
          { k: "conferencia", label: "🧪 Conferência da importação", icon: Database },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            className={`px-3 py-2 text-xs border-b-2 transition-colors ${
              tab === t.k ? "border-amber-600 text-amber-900 font-medium" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.k}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "conferencia" ? (
        <ImportAuditPanel apiCall={apiCall} />
      ) : tab === "historico" ? (
        <HistoryPanel apiCall={apiCall} items={items} />
      ) : (
      <>
      {/* Banner — revise antes de ativar */}
      {pendingReview.length > 0 && (
        <Card className="p-3 border border-amber-300 bg-amber-50/70" data-testid="pending-review-banner">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
            <div className="flex-1 text-xs">
              <div className="font-medium text-amber-900 mb-0.5">
                {pendingReview.length} automação(ões) pré-cadastrada(s) aguardando sua revisão
              </div>
              <p className="text-amber-800">
                Os modelos abaixo vieram <b>desativados de fábrica</b>. Revise a mensagem, condições e janela de envio.
                Use "Testar agora" pra disparar UMA vez antes de ativar permanentemente.
              </p>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Carregando…</Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Nenhuma automação ainda.</Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((a) => {
            const ChanIcon = (CHANNEL_META[a.action?.channel] || CHANNEL_META.whatsapp).icon;
            const chanMeta = CHANNEL_META[a.action?.channel] || CHANNEL_META.whatsapp;
            const isManual = a.trigger?.type === "manual";
            return (
              <Card key={a.automation_id} className="p-3 border border-border bg-card shadow-soft rounded-md" data-testid={`auto-card-${a.automation_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <ChanIcon className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={1.5} style={{ color: chanMeta.color }} />
                      <h3 className="text-sm font-medium truncate">{a.name}</h3>
                      {isManual ? (
                        <Badge variant="outline" style={{ background: "#fef3c7", color: "#92400e", borderColor: "transparent" }} className="text-[10px]">
                          MANUAL
                        </Badge>
                      ) : a.enabled ? (
                        <Badge variant="outline" style={{ background: "#dcfce7", color: "#15803d", borderColor: "transparent" }} className="text-[10px]">
                          ATIVA
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">DESATIVADA</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground line-clamp-2">{a.description || "—"}</p>
                  </div>
                  {!isManual && (
                    <Switch
                      checked={!!a.enabled}
                      onCheckedChange={() => toggleEnabled(a)}
                      data-testid={`auto-toggle-${a.automation_id}`}
                    />
                  )}
                </div>

                <div className="text-[11px] grid grid-cols-2 gap-1 mb-2">
                  <div className="text-muted-foreground">⏱ {TRIGGER_HUMAN(a)}</div>
                  <div className="text-muted-foreground">📨 {chanMeta.label}</div>
                  <div className="text-muted-foreground">🎯 {(a.conditions || []).length} condição(ões)</div>
                  <div className="text-muted-foreground">
                    📊 enviadas: {(a.stats?.total_sent || 0)} · erros: {(a.stats?.error_count || 0)}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditing(a)}
                    className="h-7 text-[11px] flex-1 min-w-[100px]"
                    data-testid={`auto-edit-${a.automation_id}`}
                  >
                    <SettingsIcon className="h-3 w-3 mr-1" strokeWidth={1.5} />
                    Configurar
                  </Button>
                  {isManual ? (
                    <Button
                      size="sm"
                      onClick={() => runNow(a)}
                      disabled={runningId === a.automation_id}
                      className="h-7 text-[11px] text-white"
                      style={{ background: "#16a34a" }}
                      data-testid={`auto-run-${a.automation_id}`}
                    >
                      {runningId === a.automation_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <><Send className="h-3 w-3 mr-1" strokeWidth={1.5} />Disparar agora</>
                      )}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => testNow(a)}
                      disabled={testingId === a.automation_id}
                      className="h-7 text-[11px]"
                      data-testid={`auto-test-${a.automation_id}`}
                      title="Abre /clientes com o filtro aplicado pra você revisar antes de enviar"
                    >
                      {testingId === a.automation_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <><Send className="h-3 w-3 mr-1" strokeWidth={1.5} />Pré-visualizar</>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowRunsFor(a)}
                    className="h-7 text-[11px]"
                    data-testid={`auto-runs-${a.automation_id}`}
                    title="Histórico de execuções"
                  >
                    <History className="h-3 w-3" strokeWidth={1.5} />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </>
      )}

      <EditAutomationDialog
        open={!!editing}
        automation={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />

      <RunsDialog
        open={!!showRunsFor}
        automation={showRunsFor}
        onClose={() => setShowRunsFor(null)}
      />

      <PreviewDryRunDialog
        open={!!previewData}
        data={previewData}
        onClose={() => setPreviewData(null)}
        onConfirmSend={async () => {
          // Usuário confirmou: dispara via /run-now (que tem lock anti-duplo-clique)
          if (!previewData?.automation) return;
          const a = previewData.automation;
          if (!window.confirm(`Confirma o disparo REAL de "${a.name}"? Vai enviar até ${a?.limits?.max_per_run || 200} mensagens.`)) return;
          setPreviewData(null);
          setRunningId(a.automation_id);
          try {
            const r = await apiCall("post", `/automations/${a.automation_id}/run-now`);
            const { sent = 0, matched = 0, errors = 0 } = r.data || {};
            if (sent > 0) toast.success(`${sent} mensagem(ns) enviada(s) · ${errors} erro(s)`);
            else if (matched > 0) toast.warning(`${matched} casaram mas 0 foram enviadas (provavelmente dedupe).`);
            else toast.warning("Nenhum alvo casou com as condições.");
            load();
          } catch (e) {
            toast.error(e.response?.data?.detail || "Erro ao disparar");
          } finally {
            setRunningId(null);
          }
        }}
      />

      <CreateAutomationDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </div>
  );
}

/* ============ Create Automation Dialog (visual builder) ============ */
function CreateAutomationDialog({ open, onClose, onCreated }) {
  const { apiCall } = useAuth();
  const initial = {
    name: "",
    description: "",
    triggerType: "manual",
    cron_day: "mon",
    cron_hour: "10",
    cron_minute: "0",
    cron_step_minutes: "10",
    event_name: "client.created",
    channel: "whatsapp",
    default_message: "Oi {primeiro_nome}! 🌿\n\n",
    conditions: [],
    window_start: "09:00",
    window_end: "19:00",
    dedupe_hours: 24,
    max_per_run: 200,
    context_kind: "client_filter",
  };
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (!open) setForm(initial); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!form.name.trim() || !form.default_message.trim()) {
      toast.error("Nome e mensagem são obrigatórios");
      return;
    }
    let trigger;
    if (form.triggerType === "manual") {
      trigger = { type: "manual", params: {} };
    } else if (form.triggerType === "event") {
      trigger = { type: "event", params: { event: form.event_name } };
    } else if (form.triggerType === "cron_step") {
      trigger = { type: "cron", params: { minute: `*/${parseInt(form.cron_step_minutes) || 10}` } };
    } else {
      trigger = { type: "cron", params: { day_of_week: form.cron_day, hour: parseInt(form.cron_hour), minute: parseInt(form.cron_minute) } };
    }
    setSaving(true);
    try {
      await apiCall("post", "/automations", {
        name: form.name,
        description: form.description,
        enabled: false,
        unit_id: null,
        trigger,
        conditions: form.conditions,
        action: {
          channel: form.channel,
          default_message: form.default_message,
        },
        schedule: { send_window: [form.window_start, form.window_end], days_of_week: [1,2,3,4,5,6] },
        limits: { dedupe_hours: parseInt(form.dedupe_hours) || 0, max_per_run: parseInt(form.max_per_run) || 200 },
        context_kind: form.context_kind,
      });
      toast.success(form.triggerType === "manual"
        ? "Lista dinâmica criada! Use 'Disparar agora' quando quiser enviar."
        : "Automação criada DESATIVADA. Revise e ative quando quiser.");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao criar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="create-automation-dialog">
        <DialogHeader>
          <DialogTitle>Nova automação</DialogTitle>
          <DialogDescription className="text-xs">
            Construa SE/ENTÃO no visual — sem JSON, sem código.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Nome *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Clientes sem corte há 90 dias" data-testid="new-auto-name" />
          </div>
          <div>
            <Label className="text-xs">Descrição</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-auto-desc" />
          </div>

          <div>
            <Label className="text-xs">Quando rodar (gatilho) *</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-1">
              {[
                { k: "manual", l: "📋 Lista dinâmica (manual)" },
                { k: "cron", l: "📅 Dia/Hora fixo" },
                { k: "cron_step", l: "⏱ A cada N min" },
                { k: "event", l: "⚡ Por evento" },
              ].map((t) => (
                <Button
                  key={t.k}
                  type="button"
                  size="sm"
                  variant={form.triggerType === t.k ? "default" : "outline"}
                  onClick={() => setForm({ ...form, triggerType: t.k })}
                  data-testid={`new-auto-trigger-${t.k}`}
                  className="h-8 text-[11px]"
                >
                  {t.l}
                </Button>
              ))}
            </div>
            {form.triggerType === "manual" && (
              <p className="text-[10px] text-muted-foreground mt-1">
                <ListFilter className="h-3 w-3 inline mr-0.5" strokeWidth={1.5} />
                Lista dinâmica: NÃO roda sozinha. Você clica "Disparar agora" pra enviar quando quiser. Substitui os antigos "Smart Leads".
              </p>
            )}
          </div>

          {form.triggerType === "cron" && (
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Dia(s) da semana</Label>
                <select value={form.cron_day} onChange={(e) => setForm({ ...form, cron_day: e.target.value })} className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background">
                  <option value="mon">Segunda</option>
                  <option value="tue">Terça</option>
                  <option value="wed">Quarta</option>
                  <option value="thu">Quinta</option>
                  <option value="fri">Sexta</option>
                  <option value="sat">Sábado</option>
                  <option value="mon-fri">Seg-Sex</option>
                  <option value="mon,wed,fri">Seg/Qua/Sex</option>
                  <option value="tue,fri">Ter/Sex</option>
                  <option value="*">Diariamente</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Hora</Label>
                <Input type="number" min="0" max="23" value={form.cron_hour} onChange={(e) => setForm({ ...form, cron_hour: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Minuto</Label>
                <Input type="number" min="0" max="59" value={form.cron_minute} onChange={(e) => setForm({ ...form, cron_minute: e.target.value })} />
              </div>
            </div>
          )}
          {form.triggerType === "cron_step" && (
            <div>
              <Label className="text-xs">Intervalo (minutos)</Label>
              <Input type="number" min="1" max="60" value={form.cron_step_minutes} onChange={(e) => setForm({ ...form, cron_step_minutes: e.target.value })} />
              <p className="text-[10px] text-muted-foreground mt-0.5">Bom pra "X horas antes de algo". Ex: 10 min de step + condição starts_in_hours between [23,25] = roda perto de 24h antes.</p>
            </div>
          )}
          {form.triggerType === "event" && (
            <div>
              <Label className="text-xs">Quando ocorrer o evento</Label>
              <select value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })} className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background">
                <option value="client.created">Cliente cadastrado</option>
                <option value="appointment.completed">Comanda finalizada</option>
                <option value="appointment.cancelled">Agendamento cancelado</option>
                <option value="appointment.no_show">Cliente faltou</option>
                <option value="invoice.overdue">Fiado vencido</option>
                <option value="invoice.paid">Fiado pago</option>
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Tipo de alvo</Label>
              <select value={form.context_kind} onChange={(e) => setForm({ ...form, context_kind: e.target.value })} className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background" data-testid="new-auto-context">
                <option value="client_filter">Lista de clientes (filtro)</option>
                <option value="appointment">Agendamento</option>
                <option value="invoice">Fiado/Cobrança</option>
                <option value="client">Cliente único</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Canal</Label>
              <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background">
                <option value="whatsapp">WhatsApp</option>
                <option value="email" disabled>E-mail (em breve)</option>
                <option value="app_notification" disabled>App (em breve)</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">
              <ListFilter className="h-3 w-3" strokeWidth={1.5} />
              Condições — quem entra na lista
            </Label>
            <ConditionsBuilder value={form.conditions} onChange={(c) => setForm({ ...form, conditions: c })} />
          </div>

          <RecipientsPreview contextKind={form.context_kind} conditions={form.conditions} unitId={null} />

          <div>
            <Label className="text-xs">Mensagem * (variáveis: {"{primeiro_nome}, {nome}, {hora_agendamento}, {valor_em_aberto}"})</Label>
            <Textarea rows={5} value={form.default_message} onChange={(e) => setForm({ ...form, default_message: e.target.value })} placeholder="Oi {primeiro_nome}, ..." data-testid="new-auto-message" className="font-mono text-xs" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Janela de envio - início</Label>
              <Input type="time" value={form.window_start} onChange={(e) => setForm({ ...form, window_start: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Janela de envio - fim</Label>
              <Input type="time" value={form.window_end} onChange={(e) => setForm({ ...form, window_end: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Dedupe (horas)</Label>
              <Input type="number" min="0" value={form.dedupe_hours} onChange={(e) => setForm({ ...form, dedupe_hours: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Máx por execução</Label>
              <Input type="number" min="1" value={form.max_per_run} onChange={(e) => setForm({ ...form, max_per_run: e.target.value })} />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="new-auto-save" style={{ background: "#d97706" }} className="text-white hover:opacity-90">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />Criando…</> : (form.triggerType === "manual" ? "Criar lista dinâmica" : "Criar (desativada)")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAutomationDialog({ open, automation, onClose, onSaved }) {
  const { apiCall } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && automation) {
      setForm({
        name: automation.name || "",
        description: automation.description || "",
        default_message: automation.action?.default_message || automation.action?.custom_message || "",
        funnel_status_on_run: automation.action?.funnel_status_on_run || "",
        send_window_start: automation.schedule?.send_window?.[0] || "09:00",
        send_window_end: automation.schedule?.send_window?.[1] || "19:00",
        dedupe_hours: automation.limits?.dedupe_hours ?? 24,
        max_per_run: automation.limits?.max_per_run ?? 200,
        conditions: automation.conditions || [],
      });
    } else {
      setForm(null);
    }
  }, [open, automation]);

  if (!form) return null;

  const submit = async () => {
    setSaving(true);
    try {
      const updates = {
        name: form.name,
        description: form.description,
        conditions: form.conditions,
        action: {
          ...(automation.action || {}),
          default_message: form.default_message,
          funnel_status_on_run: form.funnel_status_on_run || null,
        },
        schedule: {
          ...(automation.schedule || {}),
          send_window: [form.send_window_start, form.send_window_end],
        },
        limits: {
          ...(automation.limits || {}),
          dedupe_hours: parseInt(form.dedupe_hours) || 0,
          max_per_run: parseInt(form.max_per_run) || 200,
        },
      };
      await apiCall("patch", `/automations/${automation.automation_id}`, updates);
      toast.success("Automação salva");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="auto-edit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SettingsIcon className="h-4 w-4" strokeWidth={1.5} />
            Configurar automação
          </DialogTitle>
          <DialogDescription className="text-xs">
            Edite mensagem, condições, janela de envio e limites. Para automações ATIVAS, mudanças entram no próximo ciclo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs">Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auto-edit-name" />
          </div>
          <div>
            <Label className="text-xs">Descrição</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="auto-edit-desc" />
          </div>
          <div>
            <Label className="text-xs">Mensagem (variáveis: {"{primeiro_nome}, {nome}, {hora_agendamento}, {valor_em_aberto}"})</Label>
            <Textarea
              rows={6}
              value={form.default_message}
              onChange={(e) => setForm({ ...form, default_message: e.target.value })}
              data-testid="auto-edit-message"
              className="font-mono text-xs"
            />
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1">
              <ListFilter className="h-3 w-3" strokeWidth={1.5} />
              Condições — quem entra na lista
            </Label>
            <ConditionsBuilder
              value={form.conditions}
              onChange={(c) => setForm({ ...form, conditions: c })}
            />
          </div>

          <RecipientsPreview contextKind={automation.context_kind || "client_filter"} conditions={form.conditions} unitId={automation.unit_id} />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Janela de envio - início</Label>
              <Input type="time" value={form.send_window_start} onChange={(e) => setForm({ ...form, send_window_start: e.target.value })} data-testid="auto-edit-window-start" />
            </div>
            <div>
              <Label className="text-xs">Janela de envio - fim</Label>
              <Input type="time" value={form.send_window_end} onChange={(e) => setForm({ ...form, send_window_end: e.target.value })} data-testid="auto-edit-window-end" />
            </div>
            <div>
              <Label className="text-xs">Dedupe (horas)</Label>
              <Input type="number" min="0" value={form.dedupe_hours} onChange={(e) => setForm({ ...form, dedupe_hours: e.target.value })} data-testid="auto-edit-dedupe" />
            </div>
            <div>
              <Label className="text-xs">Máx por execução</Label>
              <Input type="number" min="1" value={form.max_per_run} onChange={(e) => setForm({ ...form, max_per_run: e.target.value })} data-testid="auto-edit-maxrun" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Atualizar funil após envio</Label>
            <select
              value={form.funnel_status_on_run}
              onChange={(e) => setForm({ ...form, funnel_status_on_run: e.target.value })}
              className="h-8 w-full px-2 text-xs border border-input rounded-md bg-background"
              data-testid="auto-edit-funnel"
            >
              <option value="">— Não alterar —</option>
              {Object.entries(FUNNEL_LABELS).map(([k, l]) => (
                <option key={k} value={k}>{l}</option>
              ))}
            </select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="auto-edit-save">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />Salvando…</> : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RunsDialog({ open, automation, onClose }) {
  const { apiCall } = useAuth();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [openRun, setOpenRun] = useState(null);

  const loadRuns = useCallback(() => {
    if (!automation) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", new Date(`${dateFrom}T00:00:00-03:00`).toISOString());
    if (dateTo)   params.set("date_to",   new Date(`${dateTo}T23:59:59-03:00`).toISOString());
    const qs = params.toString();
    apiCall("get", `/automations/${automation.automation_id}/runs${qs ? `?${qs}` : ""}`)
      .then((r) => setRuns(r.data || []))
      .catch(() => setRuns([]))
      .finally(() => setLoading(false));
  }, [apiCall, automation, dateFrom, dateTo]);

  useEffect(() => { if (open) loadRuns(); }, [open, loadRuns]);

  if (!automation) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="auto-runs-dialog">
        <DialogHeader>
          <DialogTitle>Histórico — {automation.name}</DialogTitle>
          <DialogDescription className="text-xs">Execuções e destinatários. Clique numa linha pra ver quem recebeu.</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2 flex-wrap mb-2">
          <div>
            <Label className="text-[10px] uppercase">De</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 text-xs w-36" data-testid="runs-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Até</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 text-xs w-36" data-testid="runs-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="runs-clear-date">Limpar</Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto border border-border rounded-md">
          {loading ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Carregando…</div>
          ) : runs.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Nenhuma execução no período.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-2 py-1">Início</th>
                  <th className="px-2 py-1">Matched</th>
                  <th className="px-2 py-1">Enviados</th>
                  <th className="px-2 py-1">Erros</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.run_id} className="border-t border-border/40" data-testid={`run-row-${r.run_id}`}>
                    <td className="px-2 py-1">{new Date(r.started_at).toLocaleString("pt-BR")}</td>
                    <td className="px-2 py-1">{r.matched ?? 0}</td>
                    <td className="px-2 py-1 text-emerald-700">{r.sent ?? 0}</td>
                    <td className="px-2 py-1 text-amber-700">{r.errors ?? 0}</td>
                    <td className="px-2 py-1">
                      {r.status === "ok" ? (
                        <CheckCircle2 className="h-3 w-3 text-emerald-700" strokeWidth={1.5} />
                      ) : r.status}
                    </td>
                    <td className="px-2 py-1">
                      <Button
                        variant="ghost" size="sm"
                        className="h-6 px-2 text-[10px]"
                        onClick={() => setOpenRun(r)}
                        data-testid={`run-recipients-${r.run_id}`}
                        disabled={!(r.sent || r.matched)}
                      >Destinatários</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
      <RecipientsDialog
        open={!!openRun}
        run={openRun}
        automation={automation}
        onClose={() => setOpenRun(null)}
      />
    </Dialog>
  );
}



function PreviewDryRunDialog({ open, data, onClose, onConfirmSend }) {
  if (!data) return null;
  const a = data.automation || {};
  const matched = data.matched ?? 0;
  const sample = data.first_sample;
  const preview = data.rendered_preview;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="auto-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-4 w-4 text-amber-600" strokeWidth={1.5} />
            Pré-visualização — {a.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            🔒 Esta é uma simulação. <b>Nenhuma mensagem foi enviada.</b> Pra disparar de verdade, clique em "Disparar agora" abaixo.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="p-3 rounded bg-secondary/30 border border-border">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Resultado</div>
            <div>
              {matched > 0
                ? <span className="font-medium">{matched} alvo(s) casaram com as condições</span>
                : <span className="text-amber-700">Nenhum alvo casou com as condições. Cheque os filtros.</span>}
            </div>
          </div>
          {sample && (
            <div className="p-3 rounded bg-emerald-50 border border-emerald-200">
              <div className="text-[11px] uppercase tracking-widest text-emerald-900 mb-1">Primeiro match (amostra)</div>
              <div className="text-sm font-medium">{sample.name}</div>
              <div className="text-xs text-muted-foreground">{sample.phone || "(sem telefone)"}</div>
            </div>
          )}
          {preview && (
            <div className="p-3 rounded bg-blue-50 border border-blue-200">
              <div className="text-[11px] uppercase tracking-widest text-blue-900 mb-1">Mensagem que SERIA enviada</div>
              <pre className="text-xs whitespace-pre-wrap font-sans">{preview}</pre>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="preview-cancel">
            Fechar (não envia)
          </Button>
          <Button
            onClick={onConfirmSend}
            disabled={!matched || !sample}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="preview-confirm-send"
          >
            <Send className="h-3 w-3 mr-1" strokeWidth={1.5} />
            Disparar agora ({matched})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function RecipientsDialog({ open, run, automation, onClose }) {
  const { apiCall } = useAuth();
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !run || !automation) return;
    setLoading(true);
    apiCall("get", `/automations/${automation.automation_id}/runs/${run.run_id}/sends`)
      .then((r) => setSends(r.data?.sends || []))
      .catch(() => setSends([]))
      .finally(() => setLoading(false));
  }, [open, run, automation, apiCall]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return sends;
    return sends.filter((d) =>
      (d.client_name || "").toLowerCase().includes(s) ||
      (d.phone || "").toLowerCase().includes(s),
    );
  }, [sends, search]);

  if (!run) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="recipients-dialog">
        <DialogHeader>
          <DialogTitle>Destinatários — {new Date(run.started_at).toLocaleString("pt-BR")}</DialogTitle>
          <DialogDescription className="text-xs">
            {sends.length} envio(s) registrados. Status individual conforme retorno do canal.
          </DialogDescription>
        </DialogHeader>
        <Input
          placeholder="Filtrar por nome ou telefone…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-8 text-xs"
          data-testid="recipients-search"
        />
        <div className="max-h-[420px] overflow-y-auto border border-border rounded-md">
          {loading ? (
            <div className="p-4 text-center text-xs text-muted-foreground">Carregando…</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-muted-foreground">
              {sends.length === 0 ? "Nenhum envio registrado neste run." : "Nenhum resultado pro filtro."}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-2 py-1">Cliente</th>
                  <th className="px-2 py-1">Telefone</th>
                  <th className="px-2 py-1">Status</th>
                  <th className="px-2 py-1">Enviado em</th>
                  <th className="px-2 py-1">Erro</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.send_id} className="border-t border-border/40" data-testid={`recipient-row-${d.send_id}`}>
                    <td className="px-2 py-1 max-w-[180px] truncate" title={d.client_name}>{d.client_name}</td>
                    <td className="px-2 py-1">{d.phone || "—"}</td>
                    <td className={`px-2 py-1 ${d.status === "sent" || d.status === "delivered" || d.status === "read" ? "text-emerald-700" : "text-amber-700"}`}>
                      {d.status || "—"}
                    </td>
                    <td className="px-2 py-1 text-muted-foreground">
                      {d.sent_at ? new Date(d.sent_at).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="px-2 py-1 text-red-700 max-w-[200px] truncate" title={d.last_error || ""}>
                      {d.last_error || ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============ Painel de Conferência da Importação 99 Salão ============ */
function ImportAuditPanel({ apiCall }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const refresh = () => {
    setLoading(true);
    apiCall("get", "/automations/import-audit")
      .then((r) => setData(r.data))
      .catch(() => setData({ counts: {}, relationships: {}, duplicates: {}, sample_validation: [], last_audit: null }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const runImport = async (files) => {
    if (!files.cliente_dados || !files.atendimento) {
      toast.error("Selecione pelo menos os 2 CSVs obrigatórios: cliente_dados.csv e atendimento.csv");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("cliente_dados", files.cliente_dados);
      fd.append("atendimento", files.atendimento);
      if (files.cliente_consumo) fd.append("cliente_consumo", files.cliente_consumo);
      if (files.cliente_saldo) fd.append("cliente_saldo", files.cliente_saldo);
      const r = await apiCall("post", "/automations/run-import-99salao", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Importação concluída!");
      setUploadResult(r.data);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <Card className="p-6 text-center text-sm text-muted-foreground">Carregando relatório completo…</Card>;
  if (!data) return null;
  const c = data.counts || data.current_counts || {};
  const rel = data.relationships || {};
  const dup = data.duplicates || {};
  const a = data.last_audit || null;
  const samples = data.sample_validation || [];

  const block = (label, value, hint = "", tone = "default") => (
    <div className={`p-2.5 rounded border ${tone === "warn" ? "bg-amber-50/50 border-amber-200" : tone === "ok" ? "bg-emerald-50/50 border-emerald-200" : tone === "bad" ? "bg-red-50/50 border-red-200" : "bg-card border-border"}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="text-xl font-light tabular-nums">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="import-audit-panel">
      {/* === Upload do histórico 99 Salão === */}
      <ImportUploadCard onRun={runImport} uploading={uploading} result={uploadResult} />

      {/* === Resumo principal === */}
      <Card className="p-4 border border-border bg-card shadow-soft">
        <div className="flex items-start gap-2 mb-3">
          <Database className="h-4 w-4 text-amber-700 mt-0.5" strokeWidth={1.5} />
          <div>
            <h3 className="text-sm font-medium">Conferência da base — estado atual</h3>
            <p className="text-[11px] text-muted-foreground">Contagens em tempo real. Use como verdade absoluta sobre o que existe no banco.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {block("👤 Clientes (total)", c.clients_total ?? 0)}
          {block("📥 Clientes importados", c.clients_imported ?? 0, "Tag 99salao_csv_2026")}
          {block("📞 Com telefone", c.clients_with_phone ?? 0)}
          {block("📧 Com e-mail", c.clients_with_email ?? 0)}
          {block("🎂 Com aniversário", c.clients_with_birthday ?? 0)}
          {block("🗓 Comandas (total)", c.appointments_total ?? 0)}
          {block("📥 Comandas importadas", c.appointments_imported ?? 0)}
          {block("✅ Finalizadas", c.appointments_concluido ?? 0, "status=concluido")}
          {block("📅 Finalizadas últimos 90d", c.appointments_concluido_last_90d ?? 0)}
          {block("📅 Finalizadas últimos 180d", c.appointments_concluido_last_180d ?? 0)}
          {block("🗄 Finalizadas > 180d", c.appointments_concluido_older_than_180d ?? 0, "Histórico de retorno", "ok")}
          {block("❌ Canceladas", c.appointments_cancelled ?? 0)}
          {block("👎 Faltaram (no-show)", c.appointments_no_show ?? 0)}
          {block("💳 Fiados", c.invoices_total ?? 0)}
          {block("⏳ Fiados em aberto", c.invoices_pending ?? 0, "", c.invoices_pending ? "warn" : "default")}
          {block("✅ Fiados pagos", c.invoices_paid ?? 0)}
          {block("✂️ Serviços catalogados", c.services_total ?? 0)}
          {block("📦 Produtos catalogados", c.products_total ?? 0)}
        </div>
      </Card>

      {/* === Integridade dos relacionamentos === */}
      <Card className="p-4 border border-border bg-card shadow-soft">
        <h3 className="text-sm font-medium mb-2">🔗 Integridade dos relacionamentos</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {block("Clientes válidos (com ID)", rel.valid_clients_count ?? 0, "", "ok")}
          {block("Comandas órfãs", rel.orphan_appointments ?? 0, "Sem cliente vinculado", rel.orphan_appointments ? "bad" : "ok")}
          {block("Fiados órfãos", rel.orphan_invoices ?? 0, "Sem cliente vinculado", rel.orphan_invoices ? "bad" : "ok")}
          {block("Comandas sem serviço", rel.appointments_no_services ?? 0, "Atendimento vazio", rel.appointments_no_services ? "warn" : "default")}
          {block("Comandas R$ 0,00", rel.appointments_zero_total ?? 0, "Pode ser cortesia ou erro de import", rel.appointments_zero_total > 50 ? "warn" : "default")}
        </div>
      </Card>

      {/* === Validação por amostra: campos calculados de 5 clientes aleatórios === */}
      {samples.length > 0 && (
        <Card className="p-4 border border-border bg-card shadow-soft">
          <h3 className="text-sm font-medium mb-2">🔬 Validação por amostra (5 clientes aleatórios importados)</h3>
          <p className="text-[11px] text-muted-foreground mb-2">
            Confere se os campos derivados estão calculando certo. Se "Total visitas" = 0 mas o cliente tem histórico,
            algo deu errado na vinculação.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border border-border rounded">
              <thead className="bg-secondary/40">
                <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                  <th className="px-2 py-1">Cliente</th>
                  <th className="px-2 py-1">ID 99</th>
                  <th className="px-2 py-1 text-right">Visitas</th>
                  <th className="px-2 py-1 text-right">Total gasto</th>
                  <th className="px-2 py-1 text-right">Ticket médio</th>
                  <th className="px-2 py-1 text-right">Dias s/ corte</th>
                  <th className="px-2 py-1 text-right">Dias s/ luzes</th>
                  <th className="px-2 py-1 text-right">Dias s/ visitar</th>
                  <th className="px-2 py-1">Último serviço</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((s, i) => (
                  <tr key={i} className="border-t border-border/40" data-testid={`sample-row-${i}`}>
                    <td className="px-2 py-1">{s.name || "—"}</td>
                    <td className="px-2 py-1 font-mono text-[10px] text-muted-foreground">{s.external_id_99 || "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{s.total_visits ?? 0}</td>
                    <td className="px-2 py-1 text-right tabular-nums">R$ {Number(s.total_spent || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">R$ {Number(s.avg_ticket || 0).toFixed(2)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{s.days_since_last_haircut ?? "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{s.days_since_last_highlights ?? "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{s.days_since_last_visit ?? "—"}</td>
                    <td className="px-2 py-1 truncate max-w-[150px]">{s.last_service_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* === Último import: comparativo CSV vs DB === */}
      {a && (
        <Card className="p-4 border border-border bg-card shadow-soft">
          <h3 className="text-sm font-medium mb-2">📋 Último import — {new Date(a.created_at).toLocaleString("pt-BR")}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {block("Linhas no CSV de clientes", a.cliente_dados_csv_rows ?? "?")}
            {block("Clientes novos criados", a.clients_new ?? 0, "", "ok")}
            {block("Mesclados por telefone", a.clients_merged_by_phone ?? 0, "Duplicatas resolvidas no import", "warn")}
            {block("Linhas no CSV de atendimentos", a.atendimento_csv_rows ?? "?")}
            {block("Comandas novas criadas", a.appointments_new ?? 0, "Múltiplas linhas viraram 1 comanda", "ok")}
            {block("Comandas duplicadas (puladas)", a.appointments_duplicates_skipped ?? 0)}
            {block("Comandas órfãs no import", a.appointments_orphans ?? 0, "Cliente do CSV não criado", a.appointments_orphans ? "warn" : "default")}
            {block("Erros de parsing", (a.clients_errors || 0) + (a.appointments_errors || 0), "", (a.clients_errors || a.appointments_errors) ? "warn" : "default")}
            {block("Grupos de atendimento", a.appointment_groups_total ?? "?", "CSV agrupado por cliente+data+hora")}
          </div>
        </Card>
      )}

      {/* === Duplicatas detectadas === */}
      {(dup.duplicate_phones_count > 0 || dup.duplicate_names_count > 0) && (
        <Card className="p-4 border border-amber-200 bg-amber-50/30">
          <h3 className="text-sm font-medium text-amber-900 mb-2">⚠️ Duplicatas detectadas</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {dup.duplicate_phones_count > 0 && (
              <div>
                <div className="text-xs font-medium mb-1">Mesmo telefone ({dup.duplicate_phones_count} grupos)</div>
                <div className="border border-border rounded bg-background max-h-48 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-secondary/40 sticky top-0">
                      <tr><th className="px-2 py-1 text-left">Telefone</th><th className="px-2 py-1 text-left">Qtd</th><th className="px-2 py-1 text-left">Nomes</th></tr>
                    </thead>
                    <tbody>
                      {(dup.duplicate_phones_sample || []).map((d, i) => (
                        <tr key={i} className="border-t border-border/40">
                          <td className="px-2 py-1 font-mono">{d.phone}</td>
                          <td className="px-2 py-1">{d.count}</td>
                          <td className="px-2 py-1 text-muted-foreground truncate max-w-[200px]">{(d.names || []).join(" / ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {dup.duplicate_names_count > 0 && (
              <div>
                <div className="text-xs font-medium mb-1">Mesmo nome ({dup.duplicate_names_count} grupos)</div>
                <div className="border border-border rounded bg-background max-h-48 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-secondary/40 sticky top-0">
                      <tr><th className="px-2 py-1 text-left">Nome (lowercase)</th><th className="px-2 py-1 text-left">Qtd</th></tr>
                    </thead>
                    <tbody>
                      {(dup.duplicate_names_sample || []).map((d, i) => (
                        <tr key={i} className="border-t border-border/40">
                          <td className="px-2 py-1">{d.name}</td>
                          <td className="px-2 py-1">{d.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

/* ============ Painel de Histórico de Execuções (todas as automações) ============ */
function HistoryPanel({ apiCall, items }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [openRun, setOpenRun] = useState(null);
  const [openRunAuto, setOpenRunAuto] = useState(null);

  const refresh = useCallback(() => {
    if (!items.length) { setRuns([]); setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", new Date(`${dateFrom}T00:00:00-03:00`).toISOString());
    if (dateTo)   params.set("date_to",   new Date(`${dateTo}T23:59:59-03:00`).toISOString());
    const qs = params.toString();
    Promise.all(items.map((a) =>
      apiCall("get", `/automations/${a.automation_id}/runs${qs ? `?${qs}` : ""}`).catch(() => ({ data: [] }))
    )).then((rs) => {
      const merged = [];
      rs.forEach((r, idx) => {
        (r.data || []).forEach((run) => {
          merged.push({ ...run, automation_name: items[idx].name, _auto: items[idx] });
        });
      });
      merged.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
      setRuns(merged.slice(0, 200));
    }).finally(() => setLoading(false));
  }, [apiCall, items, dateFrom, dateTo]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <Card className="p-4 border border-border bg-card shadow-soft" data-testid="history-panel">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-medium">Últimas 200 execuções (todas as automações)</h3>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase">De</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-7 text-xs w-36" data-testid="history-date-from" />
          </div>
          <div>
            <Label className="text-[10px] uppercase">Até</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-7 text-xs w-36" data-testid="history-date-to" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="history-clear-date">Limpar</Button>
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Carregando…</div>
      ) : runs.length === 0 ? (
        <div className="text-xs text-muted-foreground py-4 text-center">Nenhuma execução no período. Quando suas automações dispararem, o histórico aparece aqui.</div>
      ) : (
        <div className="border border-border rounded bg-background max-h-[500px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/40 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-2 py-1">Quando</th>
                <th className="px-2 py-1">Automação</th>
                <th className="px-2 py-1">Match</th>
                <th className="px-2 py-1">Enviadas</th>
                <th className="px-2 py-1">Erros</th>
                <th className="px-2 py-1">Origem</th>
                <th className="px-2 py-1"></th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.run_id} className="border-t border-border/40">
                  <td className="px-2 py-1">{new Date(r.started_at).toLocaleString("pt-BR")}</td>
                  <td className="px-2 py-1 truncate max-w-[200px]">{r.automation_name}</td>
                  <td className="px-2 py-1">{r.matched ?? 0}</td>
                  <td className="px-2 py-1 text-emerald-700">{r.sent ?? 0}</td>
                  <td className="px-2 py-1 text-amber-700">{r.errors ?? 0}</td>
                  <td className="px-2 py-1 text-muted-foreground text-[10px]">{r.trigger_source || "cron"}</td>
                  <td className="px-2 py-1">
                    <Button
                      variant="ghost" size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={() => { setOpenRun(r); setOpenRunAuto(r._auto); }}
                      disabled={!(r.sent || r.matched)}
                      data-testid={`history-recipients-${r.run_id}`}
                    >Quem recebeu</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RecipientsDialog
        open={!!openRun}
        run={openRun}
        automation={openRunAuto}
        onClose={() => { setOpenRun(null); setOpenRunAuto(null); }}
      />
    </Card>
  );
}

/* ============ Card de upload dos CSVs 99 Salão (self-service) ============ */
function ImportUploadCard({ onRun, uploading, result }) {
  const [files, setFiles] = useState({});
  const labels = {
    cliente_dados: { label: "cliente_dados.csv", required: true, hint: "Lista de clientes do sistema antigo (com telefones)" },
    atendimento: { label: "atendimento.csv", required: true, hint: "Histórico de comandas finalizadas" },
    cliente_consumo: { label: "cliente_consumo.csv", required: false, hint: "Opcional. Consumos consolidados por cliente" },
    cliente_saldo: { label: "cliente_saldo.csv", required: false, hint: "Opcional. Fiados/saldos em aberto" },
  };

  return (
    <Card className="p-4 border border-amber-300 bg-amber-50/40 shadow-soft" data-testid="import-upload-card">
      <div className="flex items-start gap-2 mb-3">
        <Send className="h-4 w-4 text-amber-700 mt-0.5" strokeWidth={1.5} />
        <div>
          <h3 className="text-sm font-medium text-amber-900">📥 Importar histórico do 99 Salão</h3>
          <p className="text-[11px] text-muted-foreground">
            Suba os 2 CSVs obrigatórios (clientes + atendimentos). O processo é <b>idempotente</b>: pode rodar várias
            vezes que não duplica. Clientes já existentes (mesmo telefone) serão mesclados, comandas com mesmo
            id_cliente + data + hora são puladas.
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {Object.entries(labels).map(([key, meta]) => (
          <div key={key} className={`p-2 rounded border ${files[key] ? "border-emerald-300 bg-emerald-50/40" : meta.required ? "border-amber-300 bg-background" : "border-border bg-background"}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium">{meta.label} {meta.required && <span className="text-red-600">*</span>}</span>
              {files[key] && <span className="text-[10px] text-emerald-700">✓ {(files[key].size / 1024).toFixed(0)} KB</span>}
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setFiles((prev) => ({ ...prev, [key]: e.target.files?.[0] || null }))}
              className="text-[10px] w-full"
              data-testid={`import-file-${key}`}
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">{meta.hint}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button
          type="button"
          onClick={() => onRun(files)}
          disabled={uploading || !files.cliente_dados || !files.atendimento}
          style={{ background: "#d97706" }}
          className="text-white hover:opacity-90"
          data-testid="run-import-btn"
        >
          {uploading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" strokeWidth={1.5} />Importando…</>) : "Importar histórico agora"}
        </Button>
        {(files.cliente_dados || files.atendimento) && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setFiles({})} disabled={uploading}>
            Limpar seleção
          </Button>
        )}
      </div>
      {result && (
        <div className="mt-3 p-2 rounded bg-emerald-50 border border-emerald-200 text-xs" data-testid="import-result">
          <div className="font-medium text-emerald-900">✅ {result.summary}</div>
          {result.audit && (
            <div className="text-[10px] text-muted-foreground mt-1">
              CSV clientes: {result.audit.cliente_dados_csv_rows} linhas · CSV atendimentos: {result.audit.atendimento_csv_rows} linhas · Erros: {(result.audit.clients_errors || 0) + (result.audit.appointments_errors || 0)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

